import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ChevronDown, ArrowRight, Phone, Globe as GlobeIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSEO } from '../hooks/useSEO';
// Fix Leaflet default marker icons
const dealerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
const DEALERS = [{
  id: 1,
  name: 'Robeta Toronto',
  city: 'Toronto',
  region: 'Ontario',
  lat: 43.65,
  lng: -79.38,
  phone: '(416) 555-0100',
  website: 'https://robeta-toronto.com'
}, {
  id: 2,
  name: 'Robeta Vancouver',
  city: 'Vancouver',
  region: 'British Columbia',
  lat: 49.28,
  lng: -123.12,
  phone: '(604) 555-0200',
  website: 'https://robeta-vancouver.com'
}, {
  id: 3,
  name: 'Robeta Montreal',
  city: 'Montreal',
  region: 'Quebec',
  lat: 45.5,
  lng: -73.57,
  phone: '(514) 555-0300',
  website: 'https://robeta-montreal.com'
}, {
  id: 4,
  name: 'Robeta Calgary',
  city: 'Calgary',
  region: 'Alberta',
  lat: 51.05,
  lng: -114.07,
  phone: '(403) 555-0400',
  website: 'https://robeta-calgary.com'
}, {
  id: 5,
  name: 'Robeta Los Angeles',
  city: 'Los Angeles',
  region: 'California',
  lat: 34.05,
  lng: -118.24,
  phone: '(310) 555-0500',
  website: 'https://robeta-la.com'
}, {
  id: 6,
  name: 'Robeta Miami',
  city: 'Miami',
  region: 'Florida',
  lat: 25.76,
  lng: -80.19,
  phone: '(305) 555-0600',
  website: 'https://robeta-miami.com'
}, {
  id: 7,
  name: 'Robeta Denver',
  city: 'Denver',
  region: 'Colorado',
  lat: 39.74,
  lng: -104.99,
  phone: '(720) 555-0700',
  website: 'https://robeta-denver.com'
}, {
  id: 8,
  name: 'Robeta New York',
  city: 'New York',
  region: 'New York',
  lat: 40.71,
  lng: -74.01,
  phone: '(212) 555-0800',
  website: 'https://robeta-ny.com'
}];
const REGIONS = ['All Regions', 'Ontario', 'Quebec', 'British Columbia', 'Alberta', 'California', 'Florida', 'Texas', 'New York', 'Colorado'];
export function DealerNetworkPage() {
  useSEO({
    title: 'Dealer Network — Find an Authorized Robeta Dealer',
    description: 'Find an authorized Robeta dealer near you across North America. Explore our network of certified partners in Canada and the United States.'
  });
  const [selectedRegion, setSelectedRegion] = useState('All Regions');
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const filteredDealers = useMemo(() => {
    return DEALERS.filter(dealer => {
      const matchesRegion = selectedRegion === 'All Regions' || dealer.region === selectedRegion;
      const matchesSearch = !searchQuery || dealer.city.toLowerCase().includes(searchQuery.toLowerCase()) || dealer.name.toLowerCase().includes(searchQuery.toLowerCase()) || dealer.region.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesRegion && matchesSearch;
    });
  }, [selectedRegion, searchQuery]);
  return <main className="bg-white min-h-screen pt-32 md:pt-40" data-id="element-505">
      {/* 1. Page Header */}
      <section className="container mx-auto px-6 md:px-12 mb-12" data-id="element-506">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-4xl" data-id="element-507">
          <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-508">
            Find a Dealer
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial leading-tight mb-6" data-id="element-509">
            Dealer Network
          </h1>
          <p className="text-lg md:text-xl font-light text-robeta-black/60 max-w-2xl leading-relaxed" data-id="element-510">
            Find an authorized Robeta dealer near you across North America.
          </p>
        </motion.div>
      </section>

      {/* 2. Map Section */}
      <section className="container mx-auto px-6 md:px-12 mb-24 md:mb-32" data-id="element-511">
        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="element-512">
          <div className="relative flex-1 max-w-sm" data-id="element-513">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-robeta-black/30" data-id="element-514" />
            <input type="text" placeholder="Search by city or postal code..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-11 pr-4 py-3 border border-robeta-black/10 text-sm bg-white focus:border-robeta-copper focus:outline-none transition-colors" data-id="element-515" />
          </div>
          <div className="relative w-full md:w-56" data-id="element-516">
            <select value={selectedRegion} onChange={e => setSelectedRegion(e.target.value)} className="w-full px-4 py-3 border border-robeta-black/10 text-sm font-medium text-robeta-black bg-white focus:border-robeta-copper focus:outline-none appearance-none uppercase tracking-wide cursor-pointer pr-10" data-id="element-517">
              {REGIONS.map(region => <option key={region} value={region} data-id="element-518">
                  {region}
                </option>)}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-robeta-black/30 pointer-events-none" data-id="element-519" />
          </div>
        </div>

        {/* Leaflet Map */}
        <div className="w-full h-[70vh] border border-robeta-black/5 overflow-hidden" data-id="element-520">
          <MapContainer center={[48, -100]} zoom={4} scrollWheelZoom={true} className="w-full h-full z-0" style={{
          background: '#f4f4f2'
        }} data-id="element-521">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" data-id="element-522" />
            {filteredDealers.map(dealer => <Marker key={dealer.id} position={[dealer.lat, dealer.lng]} icon={dealerIcon} data-id="element-523">
                <Popup data-id="element-524">
                  <div className="font-sans min-w-[200px]" data-id="element-525">
                    <span className="text-[#bd9c83] text-[10px] font-bold uppercase tracking-widest block mb-1" data-id="element-526">
                      Authorized Dealer
                    </span>
                    <h3 className="text-[#1f1e1d] font-bold text-sm uppercase tracking-wide mb-2" data-id="element-527">
                      {dealer.name}
                    </h3>
                    <p className="text-[#1f1e1d]/60 text-xs mb-1" data-id="element-528">
                      {dealer.city}, {dealer.region}
                    </p>
                    <a href={`tel:${dealer.phone}`} className="text-[#1f1e1d]/70 text-xs flex items-center gap-1 mb-2 hover:text-[#bd9c83]" data-id="element-529">
                      {dealer.phone}
                    </a>
                    <a href={dealer.website} target="_blank" rel="noopener noreferrer" className="text-[#bd9c83] text-[10px] font-bold uppercase tracking-widest hover:underline" data-id="element-530">
                      Visit Website →
                    </a>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>
      </section>

      {/* 3. Dealer Application CTA */}
      <section className="py-24 md:py-32 bg-robeta-mist/10" data-id="element-531">
        <div className="container mx-auto px-6 md:px-12 max-w-xl text-center" data-id="element-532">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-533">
            <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-534">
              Partnership
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-4" data-id="element-535">
              Join the Robeta Dealer Network
            </h2>
            <p className="text-robeta-black/60 font-light mb-12" data-id="element-536">
              Represent European craftsmanship in North America.
            </p>

            <form onSubmit={e => e.preventDefault()} className="space-y-4 text-left" data-id="element-537">
              <input type="email" placeholder="Your email address" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-robeta-black/10 text-sm bg-white focus:border-robeta-copper focus:outline-none transition-colors" data-id="element-538" />
              <textarea placeholder="Tell us about your dealership..." value={message} onChange={e => setMessage(e.target.value)} rows={4} className="w-full px-4 py-3 border border-robeta-black/10 text-sm bg-white focus:border-robeta-copper focus:outline-none transition-colors resize-none" data-id="element-539" />
              <button type="submit" className="w-full bg-robeta-black text-white py-4 font-bold uppercase tracking-widest text-xs hover:bg-robeta-copper transition-colors" data-id="element-540">
                Submit Inquiry
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </main>;
}